import React, { useState, useEffect } from 'react'
import {
  getDepartments,
  createDepartment,
  deleteDepartment,
  getDepartmentDetail,
  addEmployeesToDepartment,
  removeEmployeeFromDepartment,
  getPersonalEmployees,
  toggleEmployeeActive,
  changeEmployeeDept,
} from 'service/AdminService'
import styles from './DepartmentsPanel.module.scss'

const AVATAR_COLORS = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444','#06b6d4']
const avatarColor = (str = '') => {
  let h = 0
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}
const initials = (fullName = '') => fullName.trim().split(' ').slice(0, 2).map(p => p[0] || '').join('').toUpperCase()
const pluralEmployees = (n) => n === 1 ? '1 сотрудник' : n < 5 ? `${n} сотрудника` : `${n} сотрудников`

export default function DepartmentsPanel() {
  const [view, setView] = useState('list')
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDept, setSelectedDept] = useState(null)
  const [loadingDept, setLoadingDept] = useState(false)

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [creating, setCreating] = useState(false)

  const [showAddModal, setShowAddModal] = useState(false)
  const [allEmployees, setAllEmployees] = useState([])
  const [modalSearch, setModalSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [adding, setAdding] = useState(false)

  const [showChangeDeptModal, setShowChangeDeptModal] = useState(false)
  const [changeDeptTarget, setChangeDeptTarget] = useState(null)
  const [changeDeptId, setChangeDeptId] = useState('')
  const [changingDept, setChangingDept] = useState(false)

  useEffect(() => { loadDepartments() }, [])

  const loadDepartments = async () => {
    setLoading(true)
    try {
      const r = await getDepartments()
      setDepartments(r.data)
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  const openDept = async (dept) => {
    setView('detail')
    setLoadingDept(true)
    try {
      const r = await getDepartmentDetail(dept.id)
      setSelectedDept(r.data)
    } catch (e) { console.error(e) }
    finally { setLoadingDept(false) }
  }

  const backToList = () => { setView('list'); setSelectedDept(null) }

  const handleCreate = async () => {
    if (!newName.trim()) return
    setCreating(true)
    try {
      await createDepartment(newName.trim(), newDesc.trim())
      setNewName(''); setNewDesc('')
      setShowCreateModal(false)
      await loadDepartments()
    } catch (e) { console.error(e) }
    finally { setCreating(false) }
  }

  const handleDelete = async (e, id) => {
    e.stopPropagation()
    if (!window.confirm('Удалить отдел?')) return
    try {
      await deleteDepartment(id)
      await loadDepartments()
    } catch (e) { console.error(e) }
  }

  const handleRemoveEmployee = async (employeeId) => {
    if (!selectedDept) return
    try {
      await removeEmployeeFromDepartment(selectedDept.id, employeeId)
      const r = await getDepartmentDetail(selectedDept.id)
      setSelectedDept(r.data)
      await loadDepartments()
    } catch (e) { console.error(e) }
  }

  const handleToggleActive = async (emp) => {
    try {
      const r = await toggleEmployeeActive(emp.id)
      setSelectedDept(prev => ({
        ...prev,
        employees: prev.employees.map(e =>
          e.id === emp.id ? { ...e, is_active: r.data.is_active } : e
        ),
      }))
    } catch (e) { console.error(e) }
  }

  const openChangeDept = (emp) => {
    setChangeDeptTarget(emp)
    setChangeDeptId('')
    setShowChangeDeptModal(true)
  }

  const handleChangeDept = async () => {
    if (!changeDeptTarget) return
    setChangingDept(true)
    try {
      await changeEmployeeDept(changeDeptTarget.id, changeDeptId || null)
      setShowChangeDeptModal(false)
      const r = await getDepartmentDetail(selectedDept.id)
      setSelectedDept(r.data)
      await loadDepartments()
    } catch (e) { console.error(e) }
    finally { setChangingDept(false) }
  }

  const openAddModal = async () => {
    setSelectedIds([]); setModalSearch('')
    try {
      const r = await getPersonalEmployees()
      const inDept = new Set((selectedDept?.employees || []).map(e => e.id))
      setAllEmployees(r.data.filter(e => !inDept.has(e.id)))
    } catch (e) { setAllEmployees([]) }
    setShowAddModal(true)
  }

  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const handleAdd = async () => {
    if (!selectedDept || selectedIds.length === 0) return
    setAdding(true)
    try {
      await addEmployeesToDepartment(selectedDept.id, selectedIds)
      setShowAddModal(false)
      const r = await getDepartmentDetail(selectedDept.id)
      setSelectedDept(r.data)
      await loadDepartments()
    } catch (e) { console.error(e) }
    finally { setAdding(false) }
  }

  const filtered = allEmployees.filter(e => {
    const q = modalSearch.toLowerCase()
    return e.full_name?.toLowerCase().includes(q) || e.number_phone?.includes(q) || e.position?.toLowerCase().includes(q)
  })

  if (view === 'list') {
    return (
      <div className={styles.panel}>
        <div className={styles.listHeader}>
          <div>
            <h2 className={styles.pageTitle}>Отделы</h2>
            <p className={styles.pageSubtitle}>
              {loading ? '...' : `${pluralEmployees(departments.reduce((s, d) => s + (d.employee_count || 0), 0))} в ${departments.length} отделах`}
            </p>
          </div>
          <button className={styles.primaryBtn} onClick={() => setShowCreateModal(true)}>
            <span className={styles.btnIcon}>+</span>
            Создать отдел
          </button>
        </div>

        {loading && (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            Загрузка...
          </div>
        )}

        {!loading && departments.length === 0 && (
          <div className={styles.emptyWrapper}>
            <div className={styles.emptyEmoji}></div>
            <p className={styles.emptyTitle}>Отделов пока нет</p>
            <p className={styles.emptyText}>Создайте первый отдел чтобы организовать сотрудников по подразделениям</p>
            <button className={styles.primaryBtn} onClick={() => setShowCreateModal(true)}>+ Создать первый отдел</button>
          </div>
        )}

        {!loading && departments.length > 0 && (
          <div className={styles.deptGrid}>
            {departments.map(dept => (
              <div key={dept.id} className={styles.deptCard} onClick={() => openDept(dept)}>
                <div className={styles.deptCardAccent} style={{ background: avatarColor(dept.name) }} />
                <div className={styles.deptCardBody}>
                  <div className={styles.deptAvatar} style={{ background: avatarColor(dept.name) }}>
                    {dept.name[0]?.toUpperCase()}
                  </div>
                  <div className={styles.deptInfo}>
                    <p className={styles.deptName}>{dept.name}</p>
                    {dept.description && <p className={styles.deptDesc}>{dept.description}</p>}
                    <span className={styles.empBadge}>
                      <span className={styles.empBadgeDot} />
                      {pluralEmployees(dept.employee_count || 0)}
                    </span>
                  </div>
                  <div className={styles.deptCardRight}>
                    <button className={styles.iconBtn} onClick={e => handleDelete(e, dept.id)} title="Удалить отдел"></button>
                    <span className={styles.arrowIcon}></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showCreateModal && (
          <div className={styles.overlay} onClick={() => setShowCreateModal(false)}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <div className={styles.modalHeaderLeft}>
                  <div className={styles.modalIconWrap}></div>
                  <div>
                    <h3 className={styles.modalTitle}>Создать отдел</h3>
                    <p className={styles.modalSubtitle}>Введите название и описание нового отдела</p>
                  </div>
                </div>
                <button className={styles.modalClose} onClick={() => setShowCreateModal(false)}></button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Название отдела <span className={styles.required}>*</span></label>
                  <input
                    className={styles.fieldInput}
                    placeholder="Например: IT-отдел, Бухгалтерия, HR..."
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreate()}
                    autoFocus
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Описание <span className={styles.optional}>необязательно</span></label>
                  <textarea
                    className={styles.fieldTextarea}
                    placeholder="Краткое описание задач и функций отдела..."
                    value={newDesc}
                    onChange={e => setNewDesc(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button className={styles.cancelBtn} onClick={() => setShowCreateModal(false)}>Отмена</button>
                <button className={styles.confirmBtn} onClick={handleCreate} disabled={creating || !newName.trim()}>
                  {creating ? 'Создание...' : 'Создать отдел'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={styles.panel}>
      <div className={styles.detailTopBar}>
        <button className={styles.backBtn} onClick={backToList}>
          <span className={styles.backArrow}></span>
          Список отделов
        </button>
        {selectedDept && !loadingDept && (
          <button className={styles.primaryBtn} onClick={openAddModal}>
            <span className={styles.btnIcon}>+</span>
            Добавить сотрудников
          </button>
        )}
      </div>

      {loadingDept && (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          Загрузка отдела...
        </div>
      )}

      {selectedDept && !loadingDept && (
        <>
          <div className={styles.detailHero} style={{ borderLeftColor: avatarColor(selectedDept.name) }}>
            <div className={styles.detailHeroAvatar} style={{ background: avatarColor(selectedDept.name) }}>
              {selectedDept.name[0]?.toUpperCase()}
            </div>
            <div className={styles.detailHeroInfo}>
              <h2 className={styles.detailHeroName}>{selectedDept.name}</h2>
              {selectedDept.description && <p className={styles.detailHeroDesc}>{selectedDept.description}</p>}
              <span className={styles.detailHeroBadge}>{pluralEmployees(selectedDept.employees?.length || 0)}</span>
            </div>
          </div>

          {(!selectedDept.employees || selectedDept.employees.length === 0) ? (
            <div className={styles.emptyWrapper}>
              <div className={styles.emptyEmoji}></div>
              <p className={styles.emptyTitle}>В отделе нет сотрудников</p>
              <p className={styles.emptyText}>Добавьте сотрудников из общего списка</p>
              <button className={styles.primaryBtn} onClick={openAddModal}>+ Добавить сотрудников</button>
            </div>
          ) : (
            <div className={styles.empTable}>
              <div className={styles.empTableHead}>
                <span>Сотрудник</span>
                <span>Должность</span>
                <span>Отдел</span>
                <span>Телефон</span>
                <span>Доступ</span>
                <span></span>
              </div>
              {selectedDept.employees.map(emp => (
                <div key={emp.id} className={styles.empTableRow}>
                  <div className={styles.empCellName}>
                    <div className={styles.empCardAvatar} style={{ background: avatarColor(emp.full_name || 'A') }}>
                      {initials(emp.full_name)}
                    </div>
                    <span>{emp.full_name || '—'}</span>
                  </div>
                  <div className={styles.empCell}>
                    {emp.position
                      ? <span className={styles.empCardPosition}>{emp.position}</span>
                      : <span className={styles.empCellEmpty}>—</span>}
                  </div>
                  <div className={styles.empCell}>
                    <span className={styles.empCardDept}>{selectedDept.name}</span>
                  </div>
                  <div className={styles.empCell}>
                    <span className={styles.empCardPhone}>{emp.number_phone || '—'}</span>
                  </div>
                  <div className={styles.empCell}>
                    <button
                      className={emp.is_active !== false ? styles.activeToggle : styles.inactiveToggle}
                      onClick={() => handleToggleActive(emp)}
                      title={emp.is_active !== false ? 'Выключить доступ' : 'Включить доступ'}
                    >
                      <span className={styles.toggleDot} />
                      {emp.is_active !== false ? 'Активен' : 'Отключен'}
                    </button>
                  </div>
                  <div className={styles.empCellAction}>
                    <button className={styles.changeDeptBtn} onClick={() => openChangeDept(emp)} title="Сменить отдел">⇄</button>
                    <button className={styles.removeBtn} onClick={() => handleRemoveEmployee(emp.id)} title="Убрать из отдела"></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showAddModal && (
        <div className={styles.overlay} onClick={() => setShowAddModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                <div className={styles.modalIconWrap}></div>
                <div>
                  <h3 className={styles.modalTitle}>Добавить сотрудников</h3>
                  <p className={styles.modalSubtitle}>В отдел {selectedDept?.name}</p>
                </div>
              </div>
              <button className={styles.modalClose} onClick={() => setShowAddModal(false)}></button>
            </div>
            <div className={styles.modalBody}>
              <input
                className={styles.searchInput}
                placeholder=" Поиск по имени, должности, телефону..."
                value={modalSearch}
                onChange={e => setModalSearch(e.target.value)}
              />
              {selectedIds.length > 0 && <div className={styles.selectedBadge}>Выбрано: {selectedIds.length}</div>}
              {filtered.length === 0 && (
                <div style={{ padding: '24px 0', textAlign: 'center', color: '#9ca3af' }}>Нет доступных сотрудников</div>
              )}
              <div className={styles.candidateList}>
                {filtered.map(emp => (
                  <div
                    key={emp.id}
                    className={`${styles.candidateRow} ${selectedIds.includes(emp.id) ? styles.candidateRowSelected : ''}`}
                    onClick={() => toggleSelect(emp.id)}
                  >
                    <input type="checkbox" className={styles.checkBox} checked={selectedIds.includes(emp.id)}
                      onChange={() => toggleSelect(emp.id)} onClick={e => e.stopPropagation()} />
                    <div className={styles.empCardAvatar} style={{ width: 36, height: 36, fontSize: 12, background: avatarColor(emp.full_name || 'A'), flexShrink: 0 }}>
                      {initials(emp.full_name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.full_name || ''}</p>
                      <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>{emp.position || ''}  {emp.number_phone}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowAddModal(false)}>Отмена</button>
              <button className={styles.confirmBtn} onClick={handleAdd} disabled={adding || selectedIds.length === 0}>
                {adding ? 'Добавление...' : `Добавить (${selectedIds.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showChangeDeptModal && changeDeptTarget && (
        <div className={styles.overlay} onClick={() => setShowChangeDeptModal(false)}>
          <div className={styles.modal} style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                <div className={styles.modalIconWrap}>⇄</div>
                <div>
                  <h3 className={styles.modalTitle}>Сменить отдел</h3>
                  <p className={styles.modalSubtitle}>{changeDeptTarget.full_name}</p>
                </div>
              </div>
              <button className={styles.modalClose} onClick={() => setShowChangeDeptModal(false)}></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Новый отдел</label>
                <select
                  className={styles.fieldInput}
                  value={changeDeptId}
                  onChange={e => setChangeDeptId(e.target.value)}
                >
                  <option value="">— Без отдела —</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowChangeDeptModal(false)}>Отмена</button>
              <button className={styles.confirmBtn} onClick={handleChangeDept} disabled={changingDept}>
                {changingDept ? 'Перенос...' : 'Перенести'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
